import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, BookOpen, Loader2, Eye, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Nitnem', 'Banis', 'History', 'Philosophy', 'Gurpurab', 'Stories'];

const AdminKatha = () => {
  const { token } = useAuth();
  const [kathaList, setKathaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    speaker: '',
    duration: '',
    youtube_url: '',
    category: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchKatha = async () => {
    try {
      const response = await axios.get(`${API_URL}/katha`);
      setKathaList(response.data);
    } catch (error) {
      toast.error('Failed to fetch katha');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKatha();
  }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', speaker: '', duration: '', youtube_url: '', category: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/admin/katha`, form, { headers });
      toast.success('Katha added successfully');
      setDialogOpen(false);
      resetForm();
      fetchKatha();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add katha');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (kathaId) => {
    if (!window.confirm('Are you sure you want to delete this katha?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/katha/${kathaId}`, { headers });
      toast.success('Katha deleted');
      fetchKatha();
    } catch (error) {
      toast.error('Failed to delete katha');
    }
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <div data-testid="admin-katha">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Katha Library</h1>
          <p className="text-gray-600">Manage spiritual discourses and teachings</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-katha-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Katha
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Katha</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Japji Sahib - Complete Explanation"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of the katha..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="speaker">Speaker *</Label>
                  <Input
                    id="speaker"
                    value={form.speaker}
                    onChange={(e) => setForm({ ...form, speaker: e.target.value })}
                    placeholder="Speaker name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="duration">Duration *</Label>
                  <Input
                    id="duration"
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    placeholder="e.g., 2h 30m"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="youtube_url">YouTube URL</Label>
                <Input
                  id="youtube_url"
                  value={form.youtube_url}
                  onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 btn-primary" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Katha
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
        </div>
      ) : kathaList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No katha yet</h3>
            <p className="text-gray-500">Add spiritual discourses to the library</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {kathaList.map((katha) => (
            <Card key={katha.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {extractYouTubeId(katha.youtube_url) ? (
                    <img
                      src={`https://img.youtube.com/vi/${extractYouTubeId(katha.youtube_url)}/mqdefault.jpg`}
                      alt={katha.title}
                      className="w-32 h-20 object-cover rounded"
                    />
                  ) : (
                    <div className="w-32 h-20 bg-gray-100 rounded flex items-center justify-center">
                      <BookOpen className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{katha.title}</h3>
                        <p className="text-saffron-600 text-sm">{katha.speaker}</p>
                      </div>
                      <Badge variant="outline">{katha.category}</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {katha.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {katha.views || 0} views
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-red-500 hover:bg-red-50"
                    onClick={() => handleDelete(katha.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminKatha;
